import type { AcceptableValue as _AcceptableValue } from 'reka-ui';
import type { Reactive, Ref, ShallowRef } from 'vue';
import { isProxy, toRaw } from 'vue';

/**
 * Generates a union of slot key names based on a base name and an optional suffix.
 * If `Name` is a string and `Suffix` is provided, produces `"Name"` or `"Name-Suffix"`,
 * otherwise just `"Name"`. Returns `never` if `Name` is undefined.
 */
export type DynamicSlotsKeys<
  Name extends string | undefined,
  Suffix extends string | undefined = undefined
> = Name extends string ? (Suffix extends string ? Name | `${Name}-${Suffix}` : Name) : never;

/**
 * Defines a mapping of dynamic slot render functions.
 * For each slot key in `DynamicSlotsKeys<T['slot'], Suffix>`, provides a function that
 * receives props containing the corresponding item and any extra props.
 */
export type DynamicSlots<
  T extends { slot?: string },
  Suffix extends string | undefined = undefined,
  ExtraProps extends object = {}
> = {
  [K in DynamicSlotsKeys<T['slot'], Suffix>]: (
    props: {
      item: Extract<T, { slot: K extends `${infer Base}-${Suffix}` ? Base : K }>;
    } & ExtraProps
  ) => any;
};

type AllKeys<T> = T extends any ? keyof T : never;
type NonCommonKeys<T extends object> = Exclude<AllKeys<T>, keyof T>;
type PickTypeOf<T, K extends string | number | symbol> =
  K extends AllKeys<T> ? (T extends { [k in K]?: any } ? T[K] : undefined) : never;
export type MergeTypes<T extends object> = {
  [k in keyof T]: PickTypeOf<T, k>;
} & {
  [k in NonCommonKeys<T>]?: PickTypeOf<T, k>;
};

/**
 * Converts all properties of type `T` to optional string values.
 */
export type PartialString<T> = {
  [K in keyof T]?: string;
};

/**
 * A narrower version of the imported `_AcceptableValue`,
 * excluding any record types (objects with string keys).
 */
export type AcceptableValue = Exclude<_AcceptableValue, Record<string, any>>;

/**
 * Represents either a flat array of `T` or a nested array (`T[][]`).
 */
export type ArrayOrNested<T> = T[] | T[][];

/**
 * Recursively unwraps nested arrays to yield the innermost type.
 * If `T` is an array, continues unwrapping until a non-array type is reached.
 */
export type NestedItem<T> = T extends Array<infer I> ? NestedItem<I> : T;

/**
 * Extracts the keys of the innermost object type in a nested array structure.
 */
export type GetItemKeys<I> = keyof Extract<NestedItem<I>, object>;

/**
 * Retrieves the value type for a given nested item:
 * - If `VK` is undefined, returns the entire item type `T`.
 * - If `VK` is a valid key of `T`, returns the corresponding property type.
 * - Otherwise, returns `never`.
 * For non-object items, returns the item type itself.
 */
export type GetItemValue<
  I,
  VK extends GetItemKeys<I> | undefined,
  T extends NestedItem<I> = NestedItem<I>
> = T extends object ? (VK extends undefined ? T : VK extends keyof T ? T[VK] : never) : T;

/**
 * Determines the shape of `modelValue` based on:
 * - `T`: the source type or nested structure.
 * - `VK`: an optional key to select from each item.
 * - `M`: a boolean flag indicating multiple selection.
 * Returns an array of values if `M` is true, otherwise a single value.
 */
export type GetModelValue<
  T,
  VK extends GetItemKeys<T> | undefined,
  M extends boolean
> = M extends true ? GetItemValue<T, VK>[] : GetItemValue<T, VK>;

/**
 * Defines the emits interface for a component that updates its `modelValue`.
 * The `update:modelValue` event payload matches the type produced by `GetModelValue`.
 */
export interface GetModelValueEmits<T, VK extends GetItemKeys<T> | undefined, M extends boolean> {
  /** Event handler called when the value changes. */
  'update:modelValue': [payload: GetModelValue<T, VK, M>];
}

/**
 * Transforms an emits-type mapping into component props,
 * prefixing each event name with `on` and capitalizing it.
 * Each prop is a function matching the original event handlers' signature.
 */
export type EmitsToProps<T> = {
  [K in keyof T as `on${Capitalize<string & K>}`]: T[K] extends [...args: infer Args]
    ? (...args: Args) => void
    : never;
};

/**
 * Represents an indexable type where each key is mapped to its corresponding value type.
 * This is useful for creating types that can be indexed by keys of another type.
 */
export type Indexable<T> = {
  [K in keyof T]: T[K];
};

/**
 * Unwraps a value from Vue's reactivity system.
 * If the input is a reactive proxy (Ref, ShallowRef, or Reactive), it returns the raw value.
 * Otherwise, it returns the input as is.
 *
 * @param props - The value to unwrap, which can be a reactive type or a plain value.
 * @returns The unwrapped value.
 */
export function unwrap<T>(props: T | Ref<T> | ShallowRef<T> | Reactive<T>): T {
  if (isProxy(props)) {
    return toRaw(props) as T;
  }
  return props as T;
}
